import React, { SyntheticEvent, useState } from 'react';
import { ErrorMessage, Field, Formik, FormikProps } from 'formik';
import { Button, Form, Icon, Modal, Grid, Header, Checkbox, Dimmer, Loader } from 'semantic-ui-react';
import * as Yup from 'yup';

import { Container } from './styles';
import SectionLabel from '../../../../../../components/SectionLabel';
import Input from '../../../../../../components/Input';
import { adjustInsurerResponse, Offer } from './util';
import { FieldLabel } from '../../../../../../styles';
import { numberToMoneyString } from '../../../../../../services/masks';
import { editPlans } from '../../../../../../services/bond-insurance';
import { dangerNotification, successNotification, warningNotification } from '../../../../../../services/notification';
import { InsurersCodeEnum, SEGIMOB_BOND_INSURER_CODES } from '../../../../../../enums/insurers-code.enum';

export type EditPlansModalProps = {
  analysisDetails: any;
  index: number;
  analysisList: any;
  setFieldValueDetails: any;
  open: boolean;
  onClose: () => void;
};

interface FormValues {
  isManualAnalysis: boolean;
  code: string;
  offers: Offer[];
  expiredAt: string;
};

const EditPlansModal = (props: EditPlansModalProps) => {
  const { analysisDetails, open, onClose, index, analysisList, setFieldValueDetails } = props;
  const [formValues, setFormValues] = useState<Offer[]>(analysisDetails.offers);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

  const initialValues: FormValues = {
    isManualAnalysis: analysisDetails.isManualAnalysis || false,
    code: analysisDetails?.code || '',
    expiredAt: analysisDetails?.expired_at ? new Date(analysisDetails.expired_at).toISOString().split('T')[0] : '',
    offers: formValues.map((offer: any) => ({
      nomeSeguro: offer.nomeSeguro,
      valorParcela: offer.valorParcela,
      quantidadeParcela: offer.quantidadeParcela,
      rateCharge: offer?.rateCharge ?? undefined,
    })),
  };

  const handleChangeOffers = (
    index: number,
    field: keyof Offer,
    value: string | number,
    setFieldValue: (field: string, value: any
    ) => void) => {
    setFieldValue(`offers[${index}].${field}`, value);
    setFormValues(prevFormValues =>
      prevFormValues.map((offer, i) =>
        i === index ? { ...offer, [field]: value } : offer
      )
    );
  };

  const handleSubmit = async (values: FormValues, actions: any) => {
    try {
      setIsLoadingData(true);

      const offers = adjustInsurerResponse(analysisDetails.insurerResponse, analysisDetails.insurer.code, values.offers);

      const payload = {
        insurerResponse: offers,
        isManualAnalysis: values.isManualAnalysis,
        code: values.code,
        expiredAt: values.expiredAt ? new Date(values.expiredAt + 'T12:00:00Z').toISOString() : null,
      };

      const response = await editPlans(payload, analysisDetails.id);

      if (response) {
        const codeChanged =
          Boolean(values.code?.trim()) &&
          values.code !== (analysisDetails?.code || '');
        const isSegimob = SEGIMOB_BOND_INSURER_CODES.includes(
          analysisDetails?.insurer?.code
        );

        successNotification(
          'Sucesso!',
          isSegimob && codeChanged
            ? 'Código atualizado. Reintegração Segimob enfileirada — recarregue em instantes para ver os planos.'
            : 'Planos atualizados.'
        );

        setFieldValueDetails(`analysis[${index}].offers`, formValues);
        setFieldValueDetails(`analysis[${index}].code`, values.code);
        setFieldValueDetails(`analysis[${index}].isManualAnalysis`, values.isManualAnalysis);
        setFieldValueDetails(`analysis[${index}].expired_at`, values.expiredAt ? new Date(values.expiredAt + 'T12:00:00Z').toISOString() : null);
        analysisList.is_manual_analysis = values.isManualAnalysis;
        analysisList.code = values.code;
        analysisList.expired_at = values.expiredAt ? new Date(values.expiredAt + 'T12:00:00Z').toISOString() : null;
      } else {
        warningNotification('Oops...', 'Algo deu errado, tente novamente mais tarde')
      }

      actions.setSubmitting(false);
    } catch (error) {
      dangerNotification('Erro', 'Ocorreu um erro' + error);
      console.log('ERRO: ', error);
    } finally {
      setIsLoadingData(false);
      onClose();
    }
  };

  const validationSchema = Yup.object({
    code: Yup.string().test(
      'is-valid-uuid-for-pottencial',
      'Para Pottencial, o código deve ser um UUID v4 válido (ex: e273c56c-1ff3-43f6-9994-dcb1a72b3057)',
      function (value) {
        // Se não houver valor, é válido (campo opcional)
        if (!value || value.trim() === '') {
          return true;
        }

        // Validar apenas se for Pottencial
        const isPottencial = analysisDetails?.insurer?.code === 'POTTENCIAL';
        if (!isPottencial) {
          return true; // Outras seguradoras não precisam validar
        }

        // Regex para UUID v4
        const uuidv4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidv4Regex.test(value);
      }
    ),
    expiredAt: Yup.string().test(
      'is-before-bond-insurance-expired',
      'A data não pode ser superior a validade da locação/apólice',
      function (value) {
        if (!value) return true;
        
        const bondInsuranceExpiredAt = analysisDetails?.bondInsurance?.expired_at;
        if (!bondInsuranceExpiredAt) return true;

        const dateValue = new Date(value + 'T12:00:00Z');
        const bondDate = new Date(bondInsuranceExpiredAt);
        return dateValue <= bondDate;
      }
    ),
    offers: Yup.array().of(
      Yup.object({
        valorParcela: Yup.string().required('Required'),
        quantidadeParcela: Yup.number().required('Required'),
      })
    ),
  });

  const renderForm = (formikProps: FormikProps<FormValues>) => {
    const { values, setFieldValue, handleSubmit, handleChange } = formikProps;

    return (
      <Form id={'ConfirmEditPlansForm'} loading={isLoadingData} onSubmit={handleSubmit}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <Checkbox
                id={'isManualAnalysis'}
                name={'isManualAnalysis'}
                label='A análise seguirá via manual'
                checked={values.isManualAnalysis}
                onChange={handleChange}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={8}>
              <FieldLabel>Data de Expiração</FieldLabel>
              <Field
                id={'expiredAt'}
                name={'expiredAt'}
                type='date'
                value={values.expiredAt}
                onChange={handleChange}
                component={Input}
                fluid
              />
              <span style={{ fontSize: '11px', color: 'gray' }}>Deixe em branco para usar o padrão do sistema.</span>
              <div style={{ color: 'red', marginTop: '5px' }}>
                <ErrorMessage name="expiredAt" component="div" className="error-message" />
              </div>
            </Grid.Column>

            <Grid.Column width={8}>
              <FieldLabel>Código da Analise</FieldLabel>
              <Field
                id={'code'}
                name={'code'}
                type='text'
                value={values.code}
                onChange={handleChange}
                component={Input}
                fluid
                placeholder={analysisDetails?.insurer?.code === 'POTTENCIAL' ? 'UUID v4 (ex: e273c56c-1ff3-43f6...)' : ''}
              />
              <ErrorMessage name="code" component="div" className="error-message" />
            </Grid.Column>
          </Grid.Row>

          {values.offers.map((offer, index) => (
            <>
              <SectionLabel text={offer.nomeSeguro} />
              <Grid.Row key={index} style={{ marginBottom: '20px', marginTop: '0', padding: '0' }}>
                <Grid.Column width={8}>
                  <FieldLabel>Valor da Parcela</FieldLabel>
                  <Field
                    type="text"
                    name={`offers[${index}].valorParcela`}
                    value={numberToMoneyString(Number(offer.valorParcela))}
                    onChange={(_: SyntheticEvent, data: any) => {
                      const onlyNumber = data.value.replace(/\D/g, '');
                      const newValue = Number(onlyNumber) / 100;
                      handleChangeOffers(index, 'valorParcela', newValue, setFieldValue)
                    }}
                    component={Input}
                    fluid
                  />
                  <ErrorMessage name={`offers[${index}].valorParcela`} component="div" />
                </Grid.Column>
                <Grid.Column width={8}>
                  <FieldLabel>Quantidade de Parcelas</FieldLabel>
                  <Field
                    type="number"
                    name={`offers[${index}].quantidadeParcela`}
                    placeholder="Quantidade de Parcelas"
                    value={offer.quantidadeParcela}
                    component={Input}
                    onChange={(_: SyntheticEvent, data: any) => handleChangeOffers(index, 'quantidadeParcela', Number(data?.value), setFieldValue)}
                    fluid
                    min={1}
                    max={60}
                  />
                  <ErrorMessage name={`offers[${index}].quantidadeParcela`} component="div" />
                </Grid.Column>
              </Grid.Row>
            </>
          ))}
        </Grid>
      </Form>
    );
  };

  return (
    <div style={{ zIndex: 10, position: 'relative' }}>
      <Modal
        style={{
          position: "absolute", top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
        closeIcon
        open={open}
        onClose={onClose}
      >
        {isLoadingData ? (
          <Dimmer active inverted page>
            <Loader indeterminate></Loader>
          </Dimmer>
        ) : null}

        <Header icon='edit outline' content={`Editar Planos - ${analysisDetails.insurer.name}`} />
        <Modal.Content>
          <Container>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {formikProps => (renderForm(formikProps))}
            </Formik>
          </Container>
        </Modal.Content>
        <Modal.Actions>
          <Button color='red' onClick={() => onClose()}>
            <Icon name='remove' /> Fechar
          </Button>
          <Button type='submit' color='green' form={'ConfirmEditPlansForm'}>
            <Icon name='checkmark' /> Salvar
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};
export default EditPlansModal;

